<script setup>
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import KanbanList from './KanbanList.vue';
import TemplatePicker from './TemplatePicker.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const boards = useMapGetter('kanban/boards/getBoards');
const defaultBoard = useMapGetter('kanban/boards/getDefaultBoard');
const getListsByBoard = useMapGetter('kanban/lists/getListsByBoard');
const boardsUIFlags = useMapGetter('kanban/boards/getUIFlags');
const listsUIFlags = useMapGetter('kanban/lists/getUIFlags');

const activeBoard = computed(() => {
  const requestedId = route.query.board_id
    ? Number(route.query.board_id)
    : null;
  if (requestedId)
    return (
      boards.value.find(board => board.id === requestedId) || defaultBoard.value
    );
  return defaultBoard.value;
});

const listsForActiveBoard = computed(() =>
  activeBoard.value ? getListsByBoard.value(activeBoard.value.id) : []
);

// `board_id` was read from the query but nothing ever wrote it, so secondary boards were
// unreachable. Hidden for the common single-board account, where a picker is just clutter.
const hasMultipleBoards = computed(() => boards.value.length > 1);

const selectedBoardId = computed({
  get: () => activeBoard.value?.id,
  set: boardId => {
    router.push({ query: { ...route.query, board_id: boardId } });
  },
});

const isLoading = computed(
  () => boardsUIFlags.value.isFetching || listsUIFlags.value.isFetching
);

// I7: a failed fetch used to reject unhandled and leave the board looking simply empty, which
// reads as "no data" rather than "load failed".
const loadBoardData = async board => {
  if (!board) return;
  try {
    await store.dispatch('kanban/lists/get', board.id);
    await store.dispatch('kanban/cards/get', board.id);
  } catch (error) {
    useAlert(t('KANBAN.BOARD.LOAD_ERROR'));
  }
};

onMounted(async () => {
  try {
    await store.dispatch('kanban/boards/get');
  } catch (error) {
    useAlert(t('KANBAN.BOARD.LOAD_ERROR'));
  }
});

// `immediate: true` covers both the initial load (fires once on setup,
// a no-op since `activeBoard` is still undefined before boards resolve)
// and the post-fetch load (fires once when `activeBoard` becomes real).
// An explicit call in onMounted would double-fire this second case.
watch(activeBoard, loadBoardData, { immediate: true });
</script>

<template>
  <div class="flex flex-col h-full flex-1 min-w-0">
    <div
      class="flex items-center justify-between gap-3 px-4 py-2 border-b border-n-weak"
    >
      <select
        v-if="hasMultipleBoards"
        v-model="selectedBoardId"
        data-test-id="kanban-board-switcher"
        class="rounded-md border border-n-weak px-2 py-1"
        :aria-label="t('KANBAN.BOARD.SELECT_BOARD')"
      >
        <option v-for="board in boards" :key="board.id" :value="board.id">
          {{ board.name }}
        </option>
      </select>
      <span v-else />
      <router-link
        data-test-id="kanban-history-link"
        :to="{ name: 'kanban_history' }"
        class="text-body-main text-n-slate-11"
      >
        {{ t('KANBAN.HISTORY.TITLE') }}
      </router-link>
    </div>
    <div
      v-if="isLoading"
      class="flex items-center justify-center flex-1 text-n-slate-11"
    >
      {{ t('KANBAN.BOARD.LOADING') }}
    </div>
    <TemplatePicker
      v-else-if="activeBoard && listsForActiveBoard.length === 0"
      :board-id="activeBoard.id"
    />
    <div
      v-else-if="activeBoard"
      class="kanban-board-lists flex-1 flex gap-3 p-4 overflow-x-auto"
    >
      <KanbanList
        v-for="(list, index) in listsForActiveBoard"
        :key="list.id"
        :list="list"
        :is-final="index === listsForActiveBoard.length - 1"
      />
    </div>
    <div v-else class="flex items-center justify-center flex-1 text-n-slate-11">
      {{ t('KANBAN.SETTINGS.NO_BOARDS') }}
    </div>
  </div>
</template>

<style scoped>
/* Mobile browsers hide the native scrollbar until a scroll is in progress, which leaves
   no visual hint that the board scrolls horizontally. Force a persistent, thin bar. */
.kanban-board-lists {
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}

.kanban-board-lists::-webkit-scrollbar {
  height: 8px;
}

.kanban-board-lists::-webkit-scrollbar-thumb {
  @apply bg-n-slate-6;
  border-radius: 9999px;
}

.kanban-board-lists::-webkit-scrollbar-track {
  background: transparent;
}
</style>
